import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { usersApi } from "../../services/api";
import { Badge, Button, Table, PageHeader, StatCard, Input } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  ChevronDown,
  Eye,
  Trash2,
  Loader2,
  X,
  Search,
} from "lucide-react";

// Inline column filter dropdown
function ColFilter({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const active = value !== "" && value !== null && value !== undefined;

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleScrollOrResize = () => setOpen(false);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "2px 3px",
          color: active ? "#0F3D3E" : "var(--text-muted)",
          borderRadius: 4,
          transition: "color 0.15s",
        }}
      >
        {active && (
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#0F3D3E", flexShrink: 0 }} />
        )}
        <ChevronDown
          size={10}
          strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s",
          }}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              transform: "translateX(-50%)",
              zIndex: 99999,
              minWidth: 160,
              width: "max-content",
              background: "#0F3D3E",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              overflow: "hidden",
            }}
          >
            {options.map((opt) => {
              const isActive = String(value) === String(opt.value);
              return (
                <div
                  key={String(opt.value)}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#fff" : "rgba(255,255,255,0.78)",
                    background: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 0.15s",
                    letterSpacing: "0.2px",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.09)"; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  {isActive && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {opt.dot && (
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: opt.dot, flexShrink: 0 }} />
                  )}
                  {opt.label}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

// Delete confirm dialog
function ConfirmDialog({ open, name, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "var(--bg-card)", borderRadius: 14, padding: 28, maxWidth: 400, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", border: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(239,68,68,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Trash2 size={20} strokeWidth={2} color="#dc2626" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Delete User</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>This action cannot be undone easily</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 24 }}>
          Are you sure you want to delete <strong style={{ color: "var(--text-primary)" }}>{name}</strong>?
          Their data will be retained but the account will be deactivated.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)" }}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}

// Spinner
function Spinner() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <div style={{ width: 36, height: 36, margin: "0 auto 12px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading users...</div>
    </div>
  );
}

const getUserActive = (row) => {
  const s = typeof row?.status === "string" ? row.status.trim().toUpperCase() : "";
  if (s === "ACTIVE") return true;
  if (s === "INACTIVE") return false;
  if (s === "LOCKED") return false;
  if (row?.active === true || row?.isActive === true) return true;
  if (row?.active === false || row?.isActive === false) return false;
  return false;
};

const ACTIVE_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "true", label: "Active", dot: "#16a34a" },
  { value: "false", label: "Inactive", dot: "#dc2626" },
];
const GENDER_OPTIONS = [
  { value: "", label: "All Genders" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];
const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "ASTRO", label: "ASTRO" },
  { value: "IND", label: "IND" },
];
const DEVICE_TYPE_OPTIONS = [
  { value: "", label: "All Devices" },
  { value: "ANDROID", label: "Android" },
  { value: "IOS", label: "iOS" },
];

// Android SVG icon (brand color — kept as SVG since Lucide doesn't have brand icons)
function AndroidIcon() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="#59f420" aria-label="Android">
        <path d="M17.6 9.48l1.43-2.48a.5.5 0 1 0-.87-.5l-1.46 2.53A8.98 8.98 0 0 0 12 7.75c-1.68 0-3.25.46-4.57 1.28L5.97 6.5a.5.5 0 1 0-.87.5L6.53 9.48A8.96 8.96 0 0 0 3 16v3a1 1 0 0 0 1 1h1v2.25a1.25 1.25 0 1 0 2.5 0V20h9v2.25a1.25 1.25 0 1 0 2.5 0V20h1a1 1 0 0 0 1-1v-3a8.96 8.96 0 0 0-3.4-6.52ZM8.5 13.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm7 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
      </svg>
    </div>
  );
}

// Apple SVG icon (brand —kept as SVG)
function AppleIcon() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 16 16" fill="#111827" aria-label="iOS">
        <path d="M11.182.008c0 1.778-1.341 3.128-2.542 3.128-.153-1.59 1.377-3.128 2.542-3.128Zm2.885 11.33c-.37.86-.547 1.243-1.022 1.986-.664 1.04-1.602 2.334-2.765 2.345-1.033.01-1.3-.673-2.701-.665-1.4.008-1.695.678-2.728.668-1.163-.011-2.051-1.18-2.715-2.22C.278 12.442-.95 8.62.744 5.996c.829-1.285 2.31-2.04 3.702-2.04 1.418 0 2.313.677 3.487.677 1.14 0 1.836-.678 3.477-.678 1.24 0 2.556.675 3.385 1.84-3.065 1.682-2.568 6.04-.728 7.542Z" />
      </svg>
    </div>
  );
}

export default function Users() {
  const navigate = useNavigate();

  const [data, setData] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(0);
  const [activeFilter, setActiveFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (opts = {}) => {
    const p = opts.page ?? page;
    const q = opts.search ?? search;
    const active = opts.activeFilter ?? activeFilter;
    const gender = opts.genderFilter ?? genderFilter;
    const source = opts.sourceFilter ?? sourceFilter;
    const deviceType = opts.deviceTypeFilter ?? deviceTypeFilter;

    opts.isSearch ? setSearching(true) : setLoading(true);
    try {
      const [u, s] = await Promise.all([
        usersApi.getAll(q, p, 20, "createdAt", "desc",
          active !== "" ? active === "true" : null,
          gender !== "" ? gender : null,
          source !== "" ? source : null,
          deviceType !== "" ? deviceType : null
        ),
        usersApi.getStats(),
      ]);
      setData(u.data.data);
      setStats(s.data.data);
    } catch {}
    finally { opts.isSearch ? setSearching(false) : setLoading(false); }
  };

  useEffect(() => {
    const timer = setTimeout(() => { load({ page: 0, search }); setPage(0); }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    load({ page, activeFilter, genderFilter, sourceFilter, deviceTypeFilter });
  }, [page, activeFilter, genderFilter, sourceFilter, deviceTypeFilter]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); load({ page: 0, search, isSearch: true }); };

  const handleClear = () => {
    setSearch(""); setActiveFilter(""); setGenderFilter(""); setSourceFilter(""); setDeviceTypeFilter(""); setPage(0);
  };

  const setFilter = (setter, value) => { setter(value); setPage(0); };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await usersApi.delete(deleteTarget.id);
      showToast(`${deleteTarget.name} deleted successfully`);
      setDeleteTarget(null);
      load();
    } catch {
      showToast("Failed to delete user", "error");
      setDeleteTarget(null);
    }
  };

  const activeFilters = [
    activeFilter && { label: `Status: ${activeFilter === "true" ? "Active" : "Inactive"}`, clear: () => setFilter(setActiveFilter, "") },
    genderFilter && { label: `Gender: ${genderFilter}`, clear: () => setFilter(setGenderFilter, "") },
    sourceFilter && { label: `Source: ${sourceFilter}`, clear: () => setFilter(setSourceFilter, "") },
    deviceTypeFilter && { label: `Device: ${deviceTypeFilter}`, clear: () => setFilter(setDeviceTypeFilter, "") },
  ].filter(Boolean);

  const columns = [
    {
      key: "id",
      label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex((item) => item.id === v);
        return <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", minWidth: 28, display: "inline-block" }}>{page * 20 + index + 1}</span>;
      },
    },
    {
      key: "createdAt",
      label: "Reg. Date",
      render: (v) => v ? (
        <div>
          <div style={{ fontSize: 13 }}>{new Date(v).toLocaleDateString("en-IN")}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{new Date(v).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
        </div>
      ) : "—",
    },
    {
      key: "name",
      label: "User",
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#0F3D3E", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
            {(v || "?")[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 500 }}>{v || "—"}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.mobile}</div>
          </div>
        </div>
      ),
    },
    {
      key: "userId",
      label: "User ID",
      render: (v) => v ? (
        <span style={{ fontSize: 12, fontWeight: 600, color: "#0F3D3E", background: "rgba(15,61,62,0.08)", border: "1px solid rgba(15,61,62,0.15)", borderRadius: 20, padding: "3px 10px", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>{v}</span>
      ) : <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>,
    },
    {
      key: "gender",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          Gender
          <ColFilter value={genderFilter} onChange={(v) => setFilter(setGenderFilter, v)} options={GENDER_OPTIONS} />
        </div>
      ),
      render: (v) => {
        if (!v) return "—";
        const g = v.toString().toUpperCase();
        return g === "MALE" || g === "M" ? "M" : g === "FEMALE" || g === "F" ? "F" : v[0].toUpperCase();
      },
    },
    {
      key: "source",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          Source
          <ColFilter value={sourceFilter} onChange={(v) => setFilter(setSourceFilter, v)} options={SOURCE_OPTIONS} />
        </div>
      ),
      render: (v) => v ? <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{v}</span> : "—",
    },
    {
      key: "deviceType",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          Device
          <ColFilter value={deviceTypeFilter} onChange={(v) => setFilter(setDeviceTypeFilter, v)} options={DEVICE_TYPE_OPTIONS} />
        </div>
      ),
      render: (v) => {
        if (!v) return <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>;
        if (v === "ANDROID") return <AndroidIcon />;
        if (v === "IOS") return <AppleIcon />;
        return <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{v}</span>;
      },
    },
    {
      key: "active",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          Status
          <ColFilter value={activeFilter} onChange={(v) => setFilter(setActiveFilter, v)} options={ACTIVE_OPTIONS} />
        </div>
      ),
      render: (_, row) => <Badge variant={getUserActive(row) ? "success" : "danger"}>{getUserActive(row) ? "Active" : "Inactive"}</Badge>,
    },
    {
      key: "action",
      label: "Actions",
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            title="View user"
            onClick={() => navigate(`/users/wallet/${row.id}`, { state: { user: row } })}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", display: "inline-flex", alignItems: "center", padding: 4, borderRadius: 4, transition: "color 0.18s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#0F3D3E")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <Eye size={16} strokeWidth={2} />
          </button>
          <button
            title="Delete user"
            onClick={() => setDeleteTarget({ id: row.id, name: row.name || "this user" })}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", display: "inline-flex", alignItems: "center", padding: 4, borderRadius: 4, transition: "color 0.18s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <Trash2 size={16} strokeWidth={2} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <ConfirmDialog open={!!deleteTarget} name={deleteTarget?.name} onConfirm={handleDeleteConfirm} onCancel={() => setDeleteTarget(null)} />

      <PageHeader title="App Users" subtitle="Manage all registered users" />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14, marginBottom: 28 }}>
        <StatCard title="Total"    value={stats?.total ?? "—"}       gradient="linear-gradient(135deg,rgba(15,61,62,0.55),rgba(15,61,62,0.25))"    loading={loading} />
        <StatCard title="Active"   value={stats?.active ?? "—"}      gradient="linear-gradient(135deg,rgba(34,197,94,0.55),rgba(34,197,94,0.25))"  loading={loading} />
        <StatCard title="Inactive" value={stats?.inactive ?? "—"}    gradient="linear-gradient(135deg,rgba(239,68,68,0.5),rgba(239,68,68,0.2))"    loading={loading} />
        <StatCard title="ASTRO"    value={stats?.sourceAstro ?? "—"} gradient="linear-gradient(135deg,rgba(208,179,106,0.6),rgba(208,179,106,0.25))" loading={loading} />
        <StatCard title="IND"      value={stats?.sourceInd ?? "—"}   gradient="linear-gradient(135deg,rgba(59,130,246,0.5),rgba(59,130,246,0.2))"  loading={loading} />
      </div>

      <form onSubmit={handleSearch} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div style={{ position: "relative", width: "20%" }}>
          <Input
            placeholder="Search by name or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", paddingRight: search ? 32 : undefined }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              title="Clear search"
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", padding: 0, transition: "color 0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#0F3D3E")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
            >
              <X size={12} strokeWidth={3} />
            </button>
          )}
        </div>

        <Button type="submit" disabled={searching} style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
          {searching ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Loader2 size={13} strokeWidth={2} style={{ animation: "spin 0.7s linear infinite" }} />
              Searching...
            </span>
          ) : (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Search size={13} strokeWidth={2} />
              Search
            </span>
          )}
        </Button>

        {(search || activeFilters.length > 0) && (
          <Button variant="ghost" type="button" onClick={handleClear} style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            Clear all
          </Button>
        )}
      </form>

      {activeFilters.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {activeFilters.map((f, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(15,61,62,0.08)", color: "#0F3D3E", border: "1px solid rgba(15,61,62,0.2)" }}>
              {f.label}
              <button onClick={f.clear} style={{ background: "none", border: "none", cursor: "pointer", color: "#0F3D3E", display: "inline-flex", alignItems: "center", padding: 0, lineHeight: 1 }}>
                <X size={11} strokeWidth={3} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{data.totalElements} users found</span>
          {activeFilters.length > 0 && (
            <span style={{ fontSize: 11, color: "#0F3D3E", fontWeight: 600 }}>
              {activeFilters.length} filter{activeFilters.length > 1 ? "s" : ""} active
            </span>
          )}
        </div>

        {loading ? <Spinner /> : <Table columns={columns} data={data.content || []} emptyText="No users found" />}

        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {(() => {
            const totalPages = data.totalPages;
            if (totalPages <= 1 || data.totalElements === 0) return null;

            const getPages = () => {
              if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
              const pages = [];
              if (page <= 3) pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
              else if (page >= totalPages - 4) pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
              else pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
              return pages;
            };

            const btnStyle = (isActive, isDisabled) => ({
              minWidth: 34, height: 34, padding: "0 10px", display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: isActive ? "none" : "1px solid var(--border)", borderRadius: 8,
              background: isActive ? "#0F3D3E" : "transparent",
              color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
              fontSize: 13, fontWeight: isActive ? 700 : 500,
              cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.45 : 1,
              transition: "all 0.15s", fontFamily: "var(--font-body)",
            });

            return (
              <>
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={btnStyle(false, page === 0)}>← Prev</button>
                {getPages().map((pg, i) =>
                  pg === "..." ? (
                    <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}>…</span>
                  ) : (
                    <button key={pg} onClick={() => setPage(pg)} style={btnStyle(page === pg, false)}
                      onMouseEnter={(e) => { if (page !== pg) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }}
                      onMouseLeave={(e) => { if (page !== pg) e.currentTarget.style.background = "transparent"; }}>
                      {pg + 1}
                    </button>
                  )
                )}
                <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} style={btnStyle(false, page >= totalPages - 1)}>Next →</button>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}